/**
 * Desktop notifications for calls (TRUS-668).
 *
 * - Ringing: one per call (tag `call-<id>`), cleared on join / decline / when
 *   it stops ringing. Clicking it focuses the window and opens the join flow.
 * - Calls I started, while my window is in the background: "<name> joined",
 *   "<name> declined", "No answer".
 * - Respects the "Desktop notifications for calls" setting (callPrefs).
 * - Permission is asked once, from a click that starts / joins a call — never
 *   on page load.
 *
 * Browser: the Web Notifications API, so clicks arrive (onclick).
 * Desktop app (Tauri): @tauri-apps/plugin-notification cannot deliver clicks
 * on desktop, so after a call notification the window asks for attention and
 * the next time the app is activated (clicking the notification activates it)
 * the main window is shown, unminimized and focused; the ringing banner is there.
 *
 * Background ringing relies on polling (useIncomingCalls keeps polling in a
 * hidden tab). Web Push — reliable when the browser throttles a hidden tab —
 * arrives in a separate PR and complements this.
 */

import { desktopNotifications } from '@/services/desktopNotifications';
import { isSecondaryAppWindow } from '@/lib/desktopWindow';
import { callPrefsStore } from './callPrefs';
import { closeCollapsingNotifications, showCollapsingNotification } from '@/lib/browserNotifications';

const ASKED_KEY = 'weavein.calls.notifyAsked.v1';
const DENIED_HINT_KEY = 'weavein.calls.notifyDeniedHint.v1';
/** How long after a call notification an app activation still means "show the call". */
const ACTIVATION_WINDOW_MS = 120_000;

const open = new Map<string, Notification>();

export const isTauriApp = (): boolean => typeof window !== 'undefined' && '__TAURI__' in window;

function permission(): 'granted' | 'denied' | 'default' {
  try {
    return (desktopNotifications as any)?.getPermissionState?.() ?? 'granted';
  } catch {
    return 'default';
  }
}

function readFlag(key: string): boolean {
  try {
    return window.localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function setFlag(key: string) {
  try {
    window.localStorage.setItem(key, '1');
  } catch {
    /* asked again next time — harmless */
  }
}

/**
 * Ask for notification permission the first time someone starts or joins a
 * call. Call it synchronously inside the click (a permission prompt needs the
 * user gesture). No-op when already decided, the setting is off, or in a pop-out.
 */
export function maybeAskCallNotificationPermission(): void {
  if (!callPrefsStore.get().desktopNotify) return;
  if (isSecondaryAppWindow()) return;
  const ds: any = desktopNotifications;
  if (typeof ds?.getPermissionState !== 'function' || typeof ds?.requestPermission !== 'function') return;
  if (ds.getPermissionState() !== 'default') return;
  if (!isTauriApp()) {
    if (readFlag(ASKED_KEY)) return;
    setFlag(ASKED_KEY);
  }
  void Promise.resolve(ds.requestPermission()).catch(() => {});
}

/** Notifications are blocked: show the one-time hint (panel) unless already shown. */
export function shouldShowNotificationDeniedHint(): boolean {
  return callPrefsStore.get().desktopNotify && permission() === 'denied' && !readFlag(DENIED_HINT_KEY);
}

export function markNotificationDeniedHintShown() {
  setFlag(DENIED_HINT_KEY);
}

/** Bring the desktop app's window to the front (needs core:window allow-show / -unminimize / -set-focus). */
export async function focusDesktopWindow(): Promise<void> {
  if (!isTauriApp()) {
    try {
      window.focus();
    } catch {
      /* ignore */
    }
    return;
  }
  try {
    const { getCurrentWindow } = await import('@tauri-apps/api/window');
    const w = getCurrentWindow();
    await w.show().catch(() => {});
    await w.unminimize().catch(() => {});
    await w.setFocus().catch(() => {});
  } catch {
    /* not in the desktop app */
  }
}

let activationArmed: (() => void) | null = null;

/** Desktop app: notification clicks are not delivered, so the next activation focuses the window. */
function armDesktopActivation() {
  if (activationArmed || typeof window === 'undefined') return;
  const onFocus = () => {
    disarm();
    void focusDesktopWindow();
  };
  const timer = setTimeout(() => disarm(), ACTIVATION_WINDOW_MS);
  const disarm = () => {
    window.removeEventListener('focus', onFocus);
    clearTimeout(timer);
    activationArmed = null;
  };
  window.addEventListener('focus', onFocus);
  activationArmed = disarm;
  void import('@tauri-apps/api/window')
    .then(({ getCurrentWindow, UserAttentionType }) =>
      getCurrentWindow().requestUserAttention(UserAttentionType.Critical),
    )
    .catch(() => {});
}

export interface CallNotification {
  tag: string;
  title: string;
  body: string;
  /** Clicked (browser): the window is focused first. */
  onClick?: () => void;
  requireInteraction?: boolean;
  /** Also from a pop-out window (a call I'm in, popped out). */
  anyWindow?: boolean;
  data?: Record<string, unknown>;
  /**
   * Browser: the same event also arrives as a push (a ringing call). Show it
   * through the service worker with the push's tag, so the two collapse into
   * one (and skip it when the push already showed it). Its click goes to
   * `url` through the worker, routed in-app. Ignored in the desktop app.
   */
  collapseWithPush?: { url: string };
}

/** Show a call notification (setting + permission respected). Never throws. */
export async function showCallNotification(n: CallNotification): Promise<boolean> {
  if (!callPrefsStore.get().desktopNotify) return false;
  const ds: any = desktopNotifications;
  if (!ds?.showNotification) return false;
  // Never prompt from here (no user gesture): only show when already allowed.
  if (!isTauriApp() && permission() !== 'granted') return false;
  try {
    if (n.collapseWithPush && !isTauriApp()) {
      const r = await showCollapsingNotification({
        title: n.title,
        body: n.body,
        tag: n.tag,
        url: n.collapseWithPush.url,
        requireInteraction: n.requireInteraction ?? false,
        data: n.data,
        anyWindow: n.anyWindow,
      });
      if (r !== 'unavailable') return true;
    }
    const shown = await ds.showNotification({
      title: n.title,
      body: n.body,
      tag: n.tag,
      requireInteraction: n.requireInteraction ?? false,
      data: n.data,
      anyWindow: n.anyWindow,
    });
    if (isTauriApp()) {
      armDesktopActivation();
      return true;
    }
    if (shown && typeof shown === 'object') {
      open.set(n.tag, shown as Notification);
      (shown as Notification).onclick = () => {
        try {
          window.focus();
        } catch {
          /* ignore */
        }
        n.onClick?.();
        clearCallNotification(n.tag);
      };
    }
    return true;
  } catch {
    return false;
  }
}

/** Close a call notification (join / decline / it stopped ringing). */
export function clearCallNotification(tag: string): void {
  const n = open.get(tag);
  open.delete(tag);
  try {
    n?.close();
  } catch {
    /* ignore */
  }
  try {
    (desktopNotifications as any)?.clearNotificationsByTag?.(tag);
  } catch {
    /* ignore */
  }
  // Browser: also the service worker's notification for it (ours or the push's).
  if (!isTauriApp()) void closeCollapsingNotifications(tag);
}

/** The window is in the background: another tab / app is in front, or it is hidden / minimized. */
export function isWindowInBackground(): boolean {
  if (typeof document === 'undefined') return false;
  if (document.hidden) return true;
  try {
    return typeof document.hasFocus === 'function' && !document.hasFocus();
  } catch {
    return false;
  }
}

/** Tests only. */
export function __resetCallNotifications() {
  open.clear();
  activationArmed?.();
}
